import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import StatusCode from '../components/StatusCode';
import styles from '../styles/NotFound.module.css';

const NotFound: React.FC = () => {
  // Add class to body for the 404 page and ensure proper status code
  useEffect(() => {
    document.body.classList.add('page-404');
    
    // This helps with setting the status code at the HTTP level for environments 
    // where client-side code can influence the response
    if (typeof window !== 'undefined') {
      window.__setStatusCode = (code: number) => {
        // This function will be called by our StatusCode component
        console.log(`Setting status code: ${code}`);
        
        // Some hosting environments (Netlify, Vercel) look for this
        if (typeof window.netlifySetStatusCode === 'function') {
          window.netlifySetStatusCode(code);
        }
        
        // For Vercel
        if (typeof window.vercelSetStatusCode === 'function') {
          window.vercelSetStatusCode(code);
        }
      };
    }
    
    return () => {
      document.body.classList.remove('page-404');
      // Clean up
      if (typeof window !== 'undefined') {
        delete window.__setStatusCode;
      }
    };
  }, []);

  return (
    <>
      <SEO 
        title="404 - Page Not Found"
        description="The page you are looking for could not be found"
        noIndex={true}
      />
      {/* Apply StatusCode component to ensure 404 status */}
      <StatusCode code={404} />
      <style>{`
        body, html {
          background: linear-gradient(125deg, #050816 0%, #091336 100%) !important;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          min-height: 100vh;
        }
        
        /* Force header styling */
        .page-404 .header {
          background: rgba(9, 10, 35, 0.7) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
      `}</style>
      <div className={styles.notFoundContainer}>
        {/* Background stars are added via CSS */}
        <Header />
        <main className={styles.mainContent}>
          <div className={styles.messageContainer}>
            <h1 className={styles.title}>404</h1>
            <p className={styles.message}>Oops! The page you're looking for doesn't exist.</p>
            <a href="/" className={styles.homeLink}>Return to Homepage</a>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

// Extend Window interface to include our custom status code functions
declare global {
  interface Window {
    __setStatusCode?: (code: number) => void;
    netlifySetStatusCode?: (code: number) => void;
    vercelSetStatusCode?: (code: number) => void;
  }
}

export default NotFound; 